import PropTypes from "prop-types";
import * as React from "react";
import { useCallback } from "react";
import GoogleMap from "google-map-react";
import { connect } from "react-redux";
import { getCloudinaryFetchUrl } from "app/utils/image/cloudinaryUtils";
import { getGoogleMapsStaticUrlForCoords } from "app/utils/utils";
import { FormattedMessage } from "react-intl";
import Button from "app/pages/.shared/form/Button";
import "./Map.scss";
import { bindActionCreators } from "redux";
import { acceptMapPolicy } from "app/pages/.shared/Map/mapPolicyCreators";
import MapPin from "app/pages/.shared/Map/MapPin";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";

/*
 * Marge négative pour que le pin soit toujours bien positionné même au zoom/dezoom
 * @see https://github.com/istarkov/google-map-react/issues/168
 */
const PIN_STYLE = {
	height: "43px",
	width: "30px",
	marginTop: "-40px",
	marginLeft: "-20px",
};

const Map = ({
	longitude,
	latitude,
	zoom,
	mapTypeControl,
	fullscreenControl = true,
	streetViewControl,
	resolution,
	shop = "",
	googleAPIKey,
	acceptMapPolicy,
	isMapPolicyAccepted,
	autoOpen = false,
}) => {
	const { track } = useAnalytics();
	const [isGoogleMapOpen, showGoogleMap] = React.useState(false);
	const [containerDimension, setContainerDimension] = React.useState({ width: 0, height: 0 });
	const [staticMapUrl, setStaticMapUrl] = React.useState(undefined);
	const language = shop.slice(0, 2); // @see https://developers.google.com/maps/faq#languagesupport
	const region = shop.slice(3, 5);

	const handleMapSkeletonClick = useCallback(() => {
		if (!isMapPolicyAccepted) {
			acceptMapPolicy();
		}

		handleCheckMapClick();
		showGoogleMap(true);
	}, [isMapPolicyAccepted]);

	React.useEffect(() => {
		if (autoOpen && isMapPolicyAccepted) {
			showGoogleMap(true);
		}
	}, [autoOpen, isMapPolicyAccepted]);

	// https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
	const measuredRef = useCallback(node => {
		if (node !== null) {
			setContainerDimension({
				height: node.getBoundingClientRect().height,
				width: node.getBoundingClientRect().width,
			});
		}
	}, []);

	React.useEffect(() => {
		const staticMapGoogleUrl = getGoogleMapsStaticUrlForCoords({
			latitude,
			longitude,
			resolution,
			options: {
				height: containerDimension.height,
				width: containerDimension.width,
				zoom,
				googleAPIKey,
			},
		});
		const cloudinaryUrl = getCloudinaryFetchUrl(staticMapGoogleUrl);

		setStaticMapUrl(cloudinaryUrl);
	}, [latitude, longitude, resolution, containerDimension]);

	const handleCheckMapClick = useCallback(() => {
		track("productpage_click_map");
	}, []);

	if (!googleAPIKey) {
		return false;
	}

	return staticMapUrl ? (
		<div
			ref={measuredRef}
			style={{
				cursor: !isMapPolicyAccepted ? "default" : "pointer",
				height: "100%",
				width: "100%",
				background: `url(${staticMapUrl}) no-repeat center / cover`,
				backgroundSize: "cover",
				display: "flex",
			}}
			{...(isMapPolicyAccepted ? { onClick: handleMapSkeletonClick } : {})}
		>
			{isGoogleMapOpen ? (
				<GoogleMap
					options={maps => ({
						scrollwheel: false,
						fullscreenControl: fullscreenControl,
						fullscreenControlOptions: {
							position: maps.ControlPosition.BOTTOM_RIGHT,
						},
						zoomControl: true,
						zoomControlOptions: {
							position: maps.ControlPosition.TOP_RIGHT,
							style: maps.ZoomControlStyle.SMALL,
						},
						mapTypeControl: mapTypeControl,
						mapTypeControlOptions: {
							position: maps.ControlPosition.TOP_LEFT,
						},
						streetViewControl: streetViewControl,
						scaleControl: true,
					})}
					bootstrapURLKeys={{
						key: googleAPIKey,
						language,
						region,
					}}
					center={{ lat: latitude, lng: longitude }}
					zoom={zoom}
				>
					<MapPin style={PIN_STYLE} lat={latitude} lng={longitude} />
				</GoogleMap>
			) : (
				!isMapPolicyAccepted && (
					<div className="map-confidentiality">
						<div className="map-confidentiality__message">
							<Typography variant={TYPOGRAPHY_VARIANTS.LARGE}>
								<FormattedMessage id="product.page.map.confidentiality" />
							</Typography>
							<Button
								variant={BUTTON_VARIANTS.FILLED}
								onClick={handleMapSkeletonClick}
								className="map-confidentiality__button"
							>
								<FormattedMessage id="product.page.map.confidentiality.button.label" />
							</Button>
						</div>
					</div>
				)
			)}
		</div>
	) : null;
};

Map.propTypes = {
	googleAPIKey: PropTypes.string,
	mapTypeControl: PropTypes.bool,
	fullscreenControl: PropTypes.bool,
	streetViewControl: PropTypes.bool,
	shop: PropTypes.string,
	resolution: PropTypes.string,
	longitude: PropTypes.number,
	latitude: PropTypes.number,
	zoom: PropTypes.number, // 0 à 21
	acceptMapPolicy: PropTypes.func,
	isMapPolicyAccepted: PropTypes.bool,
	autoOpen: PropTypes.bool,
};

const mapStateToProps = state => {
	return {
		shop: state.shop,
		resolution: state.resolution,
		googleAPIKey: state.envVars.GOOGLE_API_KEY,
		isMapPolicyAccepted: state.isMapPolicyAccepted,
	};
};
const mapDispatchToProps = dispatch => {
	return bindActionCreators({ acceptMapPolicy }, dispatch);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(Map);
